import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, PenTool, Palette, Clock, BadgeCheck, Instagram, MessageCircle, Phone, Send, Check, Smartphone } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingContact, { WhatsAppIcon } from "@/components/FloatingContact";
import { SERVICES, FEATURED, PORTFOLIO, HERO_IMG } from "@/data/services";
import { BRAND, SLOGAN, LOGO_ICON, PHONE_DISPLAY, INSTAGRAM_URL, INSTAGRAM_HANDLE, waLink, telLink } from "@/lib/contact";
import { createInquiry, getPortfolio, getServices, fileUrl } from "@/lib/api";

const ease = [0.16, 1, 0.3, 1];
const heroLines = ["دعوتك الإلكترونية،", "بلمسة", "تُخلّد المناسبة."];

function MaskLine({ children, delay }) {
  return (
    <span className="mask-reveal">
      <motion.span className="block" initial={{ y: "115%" }} animate={{ y: "0%" }} transition={{ duration: 1, ease, delay }}>
        {children}
      </motion.span>
    </span>
  );
}

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, ease, delay }} className={className}>
      {children}
    </motion.div>
  );
}

const marqueeItems = ["دعوات زفاف", "حفلات تخرّج", "كروت إلكترونية", "مؤتمرات", "خطوبة", "مآدب خاصة"];

function Marquee() {
  return (
    <div className="relative overflow-hidden gradient-primary py-6 border-y border-white/10" data-testid="marquee">
      <div className="marquee-track">
        {[...marqueeItems, ...marqueeItems].map((t, i) => (
          <span key={i} className="mx-8 inline-flex items-center gap-8 text-3xl md:text-5xl font-serif text-white/25">
            {t}
            <span className="text-gold text-2xl">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

const chapters = [
  { n: "١", icon: PenTool, title: "تصميم مُخصّص", body: "كل دعوة تُصمّم خصيصًا لمناسبتك — ألوانك، خطوطك، وتفاصيلك الخاصة." },
  { n: "٢", icon: Palette, title: "ذوق راقٍ", body: "خطوط عربية أنيقة وتنسيق بصري يعكس فخامة مناسبتك وحضورك." },
  { n: "٣", icon: Clock, title: "تسليم سريع", body: "نُنجز تصميمك باحترافية وسرعة، جاهزًا للمشاركة عبر جوالك." },
];

const serviceIcons = [Sparkles, BadgeCheck, PenTool, Palette];

export default function Landing() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.14]);

  const [form, setForm] = useState({ name: "", phone: "", event_type: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [portfolio, setPortfolio] = useState(PORTFOLIO);
  const [services, setServices] = useState(SERVICES);
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    getPortfolio().then((data) => {
      if (Array.isArray(data) && data.length) {
        setPortfolio(data.map((d) => ({ id: d.id, img: fileUrl(d.image_path), cat: d.category, title: d.category })));
      }
    }).catch(() => {});
    getServices().then((data) => {
      if (Array.isArray(data) && data.length) setServices(data);
    }).catch(() => {});
  }, []);

  const heroImages = portfolio.length ? portfolio.map((p) => p.img) : [HERO_IMG];

  useEffect(() => {
    setHeroIndex(0);
    if (heroImages.length <= 1) return;
    const t = setInterval(() => setHeroIndex((i) => (i + 1) % heroImages.length), 3500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolio]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("يرجى إدخال الاسم");
    setSending(true);
    try {
      await createInquiry(form);
      toast.success("تم استلام طلبك! سنكمل التفاصيل عبر واتساب.");
      const msg = `مرحبًا ${BRAND} 👋\nالاسم: ${form.name}\nالمناسبة: ${form.event_type || "-"}\nالجوال: ${form.phone || "-"}\nالتفاصيل: ${form.message || "-"}`;
      window.open(waLink(msg), "_blank");
      setForm({ name: "", phone: "", event_type: "", message: "" });
    } catch {
      toast.error("تعذّر الإرسال، جرّب التواصل عبر واتساب مباشرة");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-lav bg-white px-4 py-3 text-right outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition";

  return (
    <div className="bg-ivory" id="top">
      <Navbar />
      <FloatingContact />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen gradient-primary text-white overflow-hidden grain">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.12), transparent 40%)" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-36 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} className="flex items-center gap-3 mb-7">
              <span className="h-px w-14 bg-gold" />
              <span className="text-gold text-sm tracking-[0.3em]">{BRAND} · {SLOGAN}</span>
            </motion.div>

            <h1 className="font-serif font-bold leading-[1.18] text-4xl sm:text-5xl lg:text-6xl">
              {heroLines.map((line, i) => (
                <MaskLine key={i} delay={0.35 + i * 0.18}>
                  <span className={i === 1 ? "gold-text" : ""}>{line}</span>
                </MaskLine>
              ))}
            </h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9, ease }} className="mt-7 max-w-lg text-lg text-white/75 leading-relaxed">
              في {BRAND} نُصمّم دعوات إلكترونية فاخرة لكل مناسبة. تصفّح أعمالنا واطلب تصميمك
              الخاص مباشرة عبر واتساب أو انستغرام.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.9, ease }} className="mt-9 flex flex-wrap items-center gap-4">
              <a href={waLink("مرحبًا زخرف، أرغب بطلب تصميم دعوة إلكترونية.")} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 rounded-full bg-[#25D366] px-8 py-4 font-semibold text-white transition-transform duration-300 hover:scale-[1.03]" data-testid="hero-whatsapp-btn">
                <WhatsAppIcon className="w-5 h-5" /> اطلب عبر واتساب
              </a>
              <button onClick={() => document.querySelector("#portfolio")?.scrollIntoView({ behavior: "smooth" })} className="rounded-full border border-white/25 px-8 py-4 font-medium text-white/90 hover:border-gold hover:text-gold transition-colors duration-300" data-testid="hero-portfolio-btn">
                شاهد أعمالنا
              </button>
            </motion.div>
          </div>

          {/* Hero image (rotates through our work) */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 1.1, ease }} className="relative hidden lg:block">
            <div className="clip-arch overflow-hidden shadow-2xl relative" style={{ border: "1px solid rgba(255,255,255,0.15)" }}>
              <motion.div style={{ y: imgY, scale: imgScale }} className="w-full h-[560px] relative bg-[#232a44]">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={heroImages[heroIndex]}
                    src={heroImages[heroIndex]}
                    alt="من أعمالنا"
                    initial={{ opacity: 0, scale: 1.06 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1, ease }}
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="hero-rotating-image"
                  />
                </AnimatePresence>
              </motion.div>
              <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(35,42,68,0.55))" }} />
              {heroImages.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                  {heroImages.map((_, i) => (
                    <button key={i} onClick={() => setHeroIndex(i)} className="h-1.5 rounded-full transition-all duration-300" style={{ width: i === heroIndex ? 22 : 8, background: i === heroIndex ? "#C9A24B" : "rgba(255,255,255,0.5)" }} data-testid={`hero-dot-${i}`} aria-label={`صورة ${i + 1}`} />
                  ))}
                </div>
              )}
            </div>
            <div className="absolute -bottom-6 -right-6 glass rounded-2xl px-6 py-4 border border-white/15 flex items-center gap-3">
              <img src={LOGO_ICON} alt="زخرف" className="w-12 h-12 rounded-xl object-cover ring-1 ring-gold/50" />
              <div>
                <div className="text-gold font-serif text-xl leading-none">{BRAND}</div>
                <div className="text-xs text-white/70 mt-1">{SLOGAN}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* WHY / CHAPTERS */}
      <section className="py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-16">
            <span className="text-primary text-sm tracking-[0.3em] font-semibold">لماذا {BRAND}</span>
            <h2 className="mt-4 font-serif text-3xl sm:text-5xl font-bold text-navy max-w-2xl leading-tight" style={{ color: "#232a44" }}>
              دعوة واحدة تصنع الانطباع الأول
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-10">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.12}>
                <div className="border-t-2 border-lav pt-6 h-full">
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-serif text-6xl primary-text">{c.n}</span>
                    <c.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-serif text-2xl font-semibold text-ink mb-3" style={{ color: "#20243a" }}>{c.title}</h3>
                  <p className="leading-relaxed" style={{ color: "#6c7189" }}>{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-28 gradient-primary text-white relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-6 relative">
          <Reveal className="text-center mb-14">
            <span className="text-gold text-sm tracking-[0.3em]">خدماتنا</span>
            <h2 className="mt-4 font-serif text-3xl sm:text-5xl font-bold">دعوات لكل مناسبة</h2>
          </Reveal>

          {/* FEATURED: Electronic Cards */}
          <Reveal className="mb-10">
            <div className="rounded-3xl border border-gold/40 bg-white/[0.06] p-8 sm:p-10 relative overflow-hidden" data-testid="featured-ecards">
              <span className="absolute top-6 left-6 rounded-full bg-gold px-4 py-1.5 text-xs font-bold" style={{ color: "#232a44" }}>ما يميّزنا</span>
              <div className="grid md:grid-cols-[auto,1fr] gap-8 items-center">
                <div className="w-20 h-20 rounded-2xl bg-gold/20 flex items-center justify-center shrink-0">
                  <Smartphone className="w-10 h-10 text-gold" />
                </div>
                <div>
                  <h3 className="font-serif text-3xl sm:text-4xl font-bold mb-3">{FEATURED.title}</h3>
                  <p className="text-white/75 leading-relaxed max-w-2xl mb-5">{FEATURED.desc}</p>
                  <div className="flex flex-wrap gap-x-8 gap-y-3">
                    {FEATURED.points.map((p) => (
                      <div key={p} className="flex items-center gap-2 text-sm text-white/85">
                        <Check className="w-4 h-4 text-gold" /> {p}
                      </div>
                    ))}
                  </div>
                  <a href={waLink("مرحبًا زخرف، أرغب بطلب كرت دعوة إلكتروني.")} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3 font-semibold text-white transition-transform hover:scale-[1.03]" data-testid="featured-ecards-btn">
                    <WhatsAppIcon className="w-4 h-4" /> اطلب كرتك الإلكتروني
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => {
              const Icon = serviceIcons[i % serviceIcons.length];
              return (
                <Reveal key={s.id} delay={(i % 4) * 0.08}>
                  <div className="group rounded-2xl border border-white/10 bg-white/[0.04] p-7 h-full card-lift">
                    <div className="w-12 h-12 rounded-xl bg-gold/15 flex items-center justify-center mb-5 group-hover:bg-gold/25 transition-colors">
                      <Icon className="w-6 h-6 text-gold" />
                    </div>
                    <h3 className="font-serif text-xl font-semibold mb-2">{s.title}</h3>
                    <p className="text-white/65 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2} className="text-center mt-12">
            <a href={waLink("مرحبًا زخرف، أرغب بمعرفة تفاصيل خدمات التصميم.")} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 font-semibold" style={{ color: "#3a4a78" }} data-testid="services-order-btn">
              استفسر عن خدمة <ArrowLeft className="w-4 h-4" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section id="portfolio" className="py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <span className="text-primary text-sm tracking-[0.3em] font-semibold">أعمالنا</span>
              <h2 className="mt-4 font-serif text-3xl sm:text-5xl font-bold text-navy" style={{ color: "#232a44" }}>نماذج من تصاميمنا</h2>
            </div>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary font-medium hover:text-gold transition-colors self-start" data-testid="portfolio-instagram-btn">
              <Instagram className="w-5 h-5" /> المزيد على انستغرام
            </a>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolio.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.1}>
                <a href={waLink(`مرحبًا زخرف، أعجبني هذا التصميم (${p.cat})، أريد مثله.`)} target="_blank" rel="noopener noreferrer" className="group block card-lift rounded-2xl overflow-hidden border border-lav bg-white" data-testid={`portfolio-card-${p.id}`}>
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img src={p.img} alt={p.cat} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-5" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(35,42,68,0.85))" }}>
                      <span className="flex items-center gap-2 text-white text-sm font-medium">
                        <MessageCircle className="w-4 h-4" /> اطلب تصميمًا مشابهًا
                      </span>
                    </div>
                    <span className="absolute top-4 right-4 text-[11px] rounded-full bg-white/90 px-3 py-1 font-medium" style={{ color: "#3a4a78" }}>{p.cat}</span>
                  </div>
                  <div className="p-5">
                    <div className="font-serif text-lg font-semibold" style={{ color: "#20243a" }}>{p.title}</div>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="py-28 bg-lavender">
        <div className="max-w-5xl mx-auto px-6">
          <Reveal className="text-center mb-16">
            <span className="text-primary text-sm tracking-[0.3em] font-semibold">كيف نبدأ</span>
            <h2 className="mt-4 font-serif text-3xl sm:text-5xl font-bold text-navy" style={{ color: "#232a44" }}>طلبك بثلاث خطوات</h2>
          </Reveal>
          <div className="space-y-4">
            {[
              ["تواصل معنا", "راسلنا عبر واتساب أو انستغرام وأخبرنا بتفاصيل مناسبتك."],
              ["نصمّم لك", "نُعدّ تصميمًا مخصصًا يعكس ذوقك ونعتمده معك."],
              ["استلم دعوتك", "نُسلّمك التصميم النهائي جاهزًا للمشاركة مع ضيوفك."],
            ].map(([title, body], i) => (
              <Reveal key={title} delay={i * 0.1}>
                <div className="flex items-center gap-6 rounded-2xl bg-white border border-lav p-6">
                  <div className="font-serif text-4xl primary-text w-14 shrink-0 text-center">{["١", "٢", "٣"][i]}</div>
                  <div>
                    <h3 className="font-serif text-xl font-semibold" style={{ color: "#20243a" }}>{title}</h3>
                    <p className="text-sm mt-1" style={{ color: "#6c7189" }}>{body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-28 bg-navy text-white relative overflow-hidden grain">
        <div className="max-w-6xl mx-auto px-6 relative grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="text-gold text-sm tracking-[0.3em]">تواصل معنا</span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl font-bold leading-tight">
              جاهز لدعوة <span className="gold-text">استثنائية؟</span>
            </h2>
            <p className="mt-5 text-white/65 max-w-md leading-relaxed">
              أرسل تفاصيل مناسبتك وسنعاود التواصل معك عبر واتساب لإتمام التصميم. البيع
              والتنسيق يتمّان مباشرة عبر واتساب أو انستغرام.
            </p>

            <div className="mt-8 space-y-3">
              <a href={waLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 rounded-xl bg-white/5 border border-white/10 p-4 hover:border-gold transition-colors" data-testid="contact-whatsapp">
                <span className="w-11 h-11 rounded-lg bg-[#25D366] flex items-center justify-center"><WhatsAppIcon className="w-5 h-5 text-white" /></span>
                <div><div className="text-sm text-white/60">واتساب</div><div className="font-medium">{PHONE_DISPLAY}</div></div>
              </a>
              <a href={telLink} className="flex items-center gap-4 rounded-xl bg-white/5 border border-white/10 p-4 hover:border-gold transition-colors" data-testid="contact-phone">
                <span className="w-11 h-11 rounded-lg bg-primary flex items-center justify-center"><Phone className="w-5 h-5 text-white" /></span>
                <div><div className="text-sm text-white/60">اتصال مباشر</div><div className="font-medium">{PHONE_DISPLAY}</div></div>
              </a>
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 rounded-xl bg-white/5 border border-white/10 p-4 hover:border-gold transition-colors" data-testid="contact-instagram">
                <span className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(45deg,#f09433,#dc2743,#bc1888)" }}><Instagram className="w-5 h-5 text-white" /></span>
                <div><div className="text-sm text-white/60">انستغرام</div><div className="font-medium">@{INSTAGRAM_HANDLE}</div></div>
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form onSubmit={submit} className="rounded-2xl bg-white p-8 space-y-5" data-testid="inquiry-form">
              <h3 className="font-serif text-2xl font-bold" style={{ color: "#232a44" }}>اطلب تصميمك</h3>
              <div>
                <label className="block text-sm mb-2" style={{ color: "#6c7189" }}>الاسم *</label>
                <input className={inputCls} style={{ color: "#20243a" }} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="اسمك الكريم" data-testid="inquiry-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2" style={{ color: "#6c7189" }}>رقم الجوال</label>
                  <input className={inputCls} style={{ color: "#20243a" }} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="05xxxxxxxx" data-testid="inquiry-phone-input" />
                </div>
                <div>
                  <label className="block text-sm mb-2" style={{ color: "#6c7189" }}>نوع المناسبة</label>
                  <input className={inputCls} style={{ color: "#20243a" }} value={form.event_type} onChange={(e) => set("event_type", e.target.value)} placeholder="زفاف، تخرّج..." data-testid="inquiry-event-input" />
                </div>
              </div>
              <div>
                <label className="block text-sm mb-2" style={{ color: "#6c7189" }}>تفاصيل إضافية</label>
                <textarea rows={3} className={inputCls} style={{ color: "#20243a" }} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="التاريخ، الألوان المفضلة، أي ملاحظات..." data-testid="inquiry-message-input" />
              </div>
              <button type="submit" disabled={sending} className="w-full flex items-center justify-center gap-2 rounded-xl gradient-primary text-white py-4 font-semibold disabled:opacity-60 hover:opacity-95 transition" data-testid="inquiry-submit-btn">
                {sending ? "جارٍ الإرسال..." : "إرسال ومتابعة على واتساب"} <Send className="w-4 h-4" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
